// src/redux/auth/actions.ts
// ----------------------------------------------------------------------

import { AuthActionTypes } from './constants';
import type { AppDispatch } from '../store';

/* ───────────── Tipos auxiliares ───────────── */
export interface UserData {
  id: number;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  token: string;
  email: string;
  idioma?: string;
}

/* ───────────── Helpers genéricos (para la saga) ───────────── */
export const authApiResponseSuccess = (actionType: string, data: any) => ({
  type: AuthActionTypes.API_RESPONSE_SUCCESS,
  payload: { actionType, data },
});

export const authApiResponseError = (actionType: string, error: string) => ({
  type: AuthActionTypes.API_RESPONSE_ERROR,
  payload: { actionType, error },
});

/* ───────────── Forgot-Password (thunk) ─────────────
   Acepta string u objeto { email } para compatibilidad hacia atrás.
*/
export const forgotPassword =
  (email: string) => async (dispatch: AppDispatch): Promise<void> => {
    dispatch({ type: AuthActionTypes.FORGOT_PASSWORD_REQUEST });
    try {
      const urlApi = process.env.REACT_APP_8081;
      const res = await fetch(
        `${urlApi}/ApiRest/api/auth/password-change?email=${encodeURIComponent(email)}`,
        { method: 'POST', headers: { Accept: '*/*', 'ngrok-skip-browser-warning': 'true' } }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const msg = (await res.text().catch(() => '')) || 'Hemos enviado las instrucciones a tu email.';
      // 👇 Lo que tu reducer espera:
      dispatch(authApiResponseSuccess(AuthActionTypes.FORGOT_PASSWORD_SUCCESS, { message: msg }));
    } catch (err: any) {
      dispatch(
        authApiResponseError(
          AuthActionTypes.FORGOT_PASSWORD_ERROR,
          err?.message ?? 'Error al enviar la solicitud'
        )
      );
    }
  };

/* ───────────── Signup (thunk) ───────────── */
export const signupUser =
  (fullname: string, email: string, password: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch({ type: AuthActionTypes.SIGNUP_USER });

    try {
      /* tu llamada real a la API aquí */
      const newUser: UserData = {
        id: 1,
        username: email,
        password,
        firstName: fullname.split(' ')[0],
        lastName: fullname.split(' ').slice(1).join(' '),
        role: 'user',
        token: 'dummy',
        email,
      };

      dispatch({
        type: AuthActionTypes.SIGNUP_USER_SUCCESS,
        payload: { user: newUser },
      });
    } catch (err: any) {
      dispatch(authApiResponseError(AuthActionTypes.SIGNUP_USER, err.message));
    }
  };

/* ───────────── Login / Logout ───────────── */
export const loginUser = (username: string, password: string) => ({
  type: AuthActionTypes.LOGIN_USER,
  payload: { username, password },
});

export const logoutUser = () => ({
  type: AuthActionTypes.LOGOUT_USER,
});

/* ───────────── Google Login ───────────── */
export const loginUserWithGoogle = (token: string) => ({
  type: AuthActionTypes.LOGIN_USER_WITH_GOOGLE,
  payload: { token },
});

export const loginUserWithGoogleSuccess = (user: UserData) => ({
  type: AuthActionTypes.LOGIN_USER_WITH_GOOGLE_SUCCESS,
  payload: { user },
});

export const loginUserWithGoogleFailure = (error: string) => ({
  type: AuthActionTypes.LOGIN_USER_WITH_GOOGLE_FAILURE,
  payload: { error },
});

/* ───────────── Restaurar sesión (App.tsx) ───────────── */
export const restoreUserSession = (user: UserData) => ({
  type: AuthActionTypes.RESTORE_SESSION,
  payload: { user },
});

/* ───────────── Prefijo BD y CVL padre ───────────── */
export const updateDatabasePrefix = (prefix: string) => ({
  type: AuthActionTypes.UPDATE_DATABASE_PREFIX,
  payload: { prefix },
});

export const setParentCvl = (cvl: string) => ({
  type: AuthActionTypes.SET_PARENT_CVL,
  payload: { parentCvl: cvl },
});

/* ───────────── Reset genérico ───────────── */
export const resetAuth = () => ({ type: AuthActionTypes.RESET });
